import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from 'react-tooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { IconBadge, SectionCard, StatusBadge } from '../../components/ui';
import { formatCurrency } from '../../utils/baseUtils';

// A year in minutes — the sanity bound above which `webhookAgeMinutes` is
// not a real duration but PlatformFinanceService.cs's GetHealthAsync
// deliberately sending TimeSpan.MaxValue for "no ConnectWebhookEvent row
// exists at all" (never received one, not merely a stale one). Rendered as
// raw minutes that reads as "Webhook 15250284452471 dk sessiz" — a 14-digit
// number in the exact chip built to warn about this. Below the bound, the
// age is still shown in hours or days rather than raw minutes: a genuine
// first silence (e.g. 380 minutes) is far more readable as "~6h" than as a
// three-digit minute count.
const MAX_SANE_WEBHOOK_AGE_MINUTES = 60 * 24 * 365;

const formatWebhookAge = (minutes) => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes > MAX_SANE_WEBHOOK_AGE_MINUTES) {
    return 'No webhook ever received';
  }
  if (minutes >= 60 * 24) {
    const days = Math.round(minutes / (60 * 24));
    return `Webhook silent for ${days} ${days === 1 ? 'day' : 'days'}`;
  }
  if (minutes >= 60) {
    const hours = Math.round(minutes / 60);
    return `Webhook silent for ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }
  return `Webhook silent for ${Math.round(minutes)} min`;
};

// Spec §4's three signals, each named the way an admin who has never seen
// the DTO would still recognise: how many, of what. `hardSignals` (not the
// raw counts) decides which of these render — a count can be non-zero while
// its signal is stale API state the projection didn't actually raise, so a
// label only appears for a code the API put in the array.
// What each reading means, in the admin's words (owner, 2026-09-24: "ne
// işe yaradığı"). Shared with the Overview's Fee health tile, so the two
// never explain the same figure two ways. The fee figures are the API's
// rolling 30-day window (PlatformHealthDto), not this month.
export const HEALTH_HELP = {
  healthy:
    'Healthy: every Connect account can take payments and receive payouts, and Stripe\'s webhook has reported recently. Nothing needs you right now.',
  critical:
    'Critical: at least one hard signal — a restricted account, payouts disabled, or a webhook that has gone silent. The chips say which; each needs action.',
  unreadable:
    'Health could not be checked: the read failed, so nothing here can be trusted as fine. Try again, or check the API.',
  averageDelta:
    'Average delta: how far the Stripe fee we estimated at checkout was from what Stripe actually charged, averaged per payment over the last 30 days. Close to $0.00 means our fee estimates are accurate.',
  feesWaived:
    'Fees waived: platform fees we could not collect over the last 30 days — for example when an order was too small to carry its fee. Money we earned but did not receive.',
};

const TOOLTIP_ID = 'health-strip-tooltip';

const Info = ({ label, text }) => (
  <button
    type="button"
    aria-label={`About ${label}`}
    data-tooltip-id={TOOLTIP_ID}
    data-tooltip-content={text}
    className="inline-flex items-center justify-center rounded-full text-slate-400 transition hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200 dark:hover:text-sky-400"
  >
    <InformationCircleIcon strokeWidth={2} className="h-4 w-4" />
  </button>
);

export const HARD_SIGNAL_LABELS = {
  charges_disabled: (health) =>
    `${health.chargesDisabledCount} restricted ${health.chargesDisabledCount === 1 ? 'account' : 'accounts'}`,
  payouts_disabled: (health) =>
    `${health.payoutsDisabledCount} ${health.payoutsDisabledCount === 1 ? 'payout' : 'payouts'} disabled`,
  webhook_silent: (health) => formatWebhookAge(health.webhookAgeMinutes),
};

const SoftCounter = ({ label, value, help }) => (
  <div className="flex flex-col gap-0.5">
    <p className="flex items-center gap-1 text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
      {label}
      {help ? <Info label={label} text={help} /> : null}
    </p>
    <p className="tabular-nums text-lg font-semibold text-slate-900 dark:text-slate-100">
      {formatCurrency(value)}
    </p>
  </div>
);

// The top-of-page answer to "is anything broken right now". Three states,
// never blended: an unreadable result is its own neutral state rather than
// a healthy one with missing numbers — Global Constraints, and the reason
// this file exists — a hard signal is an alarm naming exactly what fired,
// and anything else is healthy with the soft counters shown alongside, not
// dressed up as an emergency.
const HealthStrip = ({ health }) => {
  // No object yet (still loading, or the fetch hasn't started) is not the
  // same claim as "the read failed" — it gets its own quiet placeholder
  // rather than either color, and definitely not the unreadable copy, which
  // says something did come back and explain itself.
  if (!health) {
    return (
      <SectionCard
        className="border-slate-200 dark:border-slate-700"
        bodyClassName="flex items-center gap-3"
      >
        <IconBadge icon={QuestionMarkCircleIcon} tone="slate" />
        <p className="text-sm text-slate-500 dark:text-slate-400">Loading health data…</p>
      </SectionCard>
    );
  }

  if (health.readable === false) {
    return (
      <SectionCard
        className="border-amber-200 dark:border-amber-900/50"
        bodyClassName="flex items-start gap-3"
      >
        <IconBadge icon={ExclamationTriangleIcon} tone="amber" />
        <div>
          <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-300">
            Health could not be checked
            <Info label="Health could not be checked" text={HEALTH_HELP.unreadable} />
          </p>
          <Tooltip id={TOOLTIP_ID} className="max-w-xs text-xs" />
          <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
            {health.reason || 'No reason given.'}
          </p>
        </div>
      </SectionCard>
    );
  }

  const hardSignals = Array.isArray(health.hardSignals) ? health.hardSignals : [];

  if (health.hasHardSignal) {
    return (
      <SectionCard
        className="border-rose-200 dark:border-rose-900/50"
        bodyClassName="flex flex-wrap items-center gap-3"
      >
        <IconBadge icon={ExclamationTriangleIcon} tone="rose" />
        <p className="flex items-center gap-1.5 text-sm font-semibold text-rose-800 dark:text-rose-300">
          Critical
          <Info label="Critical" text={HEALTH_HELP.critical} />
        </p>
        <Tooltip id={TOOLTIP_ID} className="max-w-xs text-xs" />
        <div className="flex flex-wrap items-center gap-2">
          {hardSignals.map((code) => {
            const describe = HARD_SIGNAL_LABELS[code];
            if (!describe) return null;
            return <StatusBadge key={code} tone="rose" label={describe(health)} dot />;
          })}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      className="border-emerald-200 dark:border-emerald-900/50"
      bodyClassName="flex flex-wrap items-center gap-6"
    >
      <div className="flex items-center gap-3">
        <IconBadge icon={CheckCircleIcon} tone="emerald" />
        <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800 dark:text-emerald-300">
          Healthy
          <Info label="Healthy" text={HEALTH_HELP.healthy} />
        </p>
      </div>
      <SoftCounter label="Average delta" value={health.averageDelta} help={HEALTH_HELP.averageDelta} />
      <SoftCounter label="Fees waived" value={health.feesNotCollected} help={HEALTH_HELP.feesWaived} />
      <Tooltip id={TOOLTIP_ID} className="max-w-xs text-xs" />
    </SectionCard>
  );
};

export default HealthStrip;
